import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import MakeRequest from '../Components/Helpers/MakeRequest';
import { Link, useOutletContext } from 'react-router-dom';
import QuillExports from '.././Components/Helpers/QuillHelpers';
import JSZip from "jszip";
import { saveAs } from "file-saver";

const About = () => {
  const { l } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // selectedSheets is a Map<sheetKey, { url, name }>
  const [selectedSheets, setSelectedSheets] = useState(new Map());

  // NEW: filters
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // View mode toggle
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await MakeRequest({}, 'content/products', 'get');
        setProducts(response.data.filter(p => p.name));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Helper: normalize/ci compare
  const norm = v => (v ?? '').toString().trim().toLowerCase();
  const passesFilters = (sheet) => {
    const langOk = selectedLanguage === 'all' || norm(sheet.language) === norm(selectedLanguage);
    const typeOk = selectedType === 'all' || norm(sheet.type) === norm(selectedType);
    return langOk && typeOk;
  };

  // Derive unique Languages/Types from data for dropdowns
  const { languages, types } = useMemo(() => {
    const langs = new Set();
    const tys = new Set();
    for (const p of products || []) {
      for (const s of (p?.safetySheets || [])) {
        if (s?.language) langs.add(norm(s.language));
        if (s?.type) tys.add(norm(s.type));
      }
    }
    // Keep original case nice for display by capitalizing
    const cap = (x) => x ? x.charAt(0).toUpperCase() + x.slice(1) : x;
    return {
      languages: ['all', ...Array.from(langs).sort()],
      types: ['all', ...Array.from(tys).sort()],
    };
  }, [products]);

  const toggleSheet = (sheetKey, sheetData) => {
    setSelectedSheets(prev => {
      const next = new Map(prev);
      if (next.has(sheetKey)) {
        next.delete(sheetKey);
      } else {
        next.set(sheetKey, sheetData);
      }
      return next;
    });
  };

  // Returns a function that tells whether all *filtered* sheets for a product are selected
  const productAllSelected = useMemo(() => {
    return (product, pIndex) => {
      const pid = product?.id ?? `p-${pIndex}`;
      const sheets = (product?.safetySheets ?? []).filter(passesFilters);
      if (!sheets.length) return false;
      return sheets.every((_, sIdx) => selectedSheets.has(`${pid}-${sIdx}`));
    };
    // include filters & selectedSheets to keep it in sync
  }, [selectedSheets, selectedLanguage, selectedType]);

  const toggleSelectAllForProduct = (product, pIndex) => {
    const pid = product?.id ?? `p-${pIndex}`;
    const allSheets = product?.safetySheets ?? [];
    const sheets = allSheets
      .map((s, idx) => ({ sheet: s, idx }))
      .filter(({ sheet }) => passesFilters(sheet));

    const allSelected = productAllSelected(product, pIndex);

    setSelectedSheets(prev => {
      const next = new Map(prev);
      sheets.forEach(({ sheet, idx }) => {
        const key = `${pid}-${idx}`;
        if (allSelected) {
          // unselect filtered for this product
          next.delete(key);
        } else {
          // NOTE: use `file` as per your object shape
          next.set(key, { url: sheet.file, name: sheet.name, productName: product.name });
        }
      });
      return next;
    });
  };

  const clearAll = () => setSelectedSheets(new Map());

  const handleDownloadSelected = async () => {
    if (!selectedSheets || !(selectedSheets instanceof Map) || selectedSheets.size === 0) return;

    const zip = new JSZip();

    // Fetch in parallel for speed
    const entries = Array.from(selectedSheets.values()); // [{url, name}, ...]
    const results = await Promise.allSettled(
      entries.map(async ({ url, name }) => {
        const res = await fetch(url, { mode: "cors" }); // adjust if needed
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const base = (name || url.split("/").pop() || "file").trim();
        return { blob, base };
      })
    );

    // Add to zip (and de-dupe filenames if collisions)
    const used = new Map(); // base -> count
    for (const r of results) {
      if (r.status !== "fulfilled") continue;
      const { blob, base } = r.value;

      const parts = base.split(".");
      const ext = parts.length > 1 ? "." + parts.pop() : "";
      const stem = parts.join(".");

      const count = (used.get(base) ?? 0) + 1;
      used.set(base, count);

      const finalName = count === 1 ? base : `${stem} (${count})${ext}`;
      zip.file(finalName, blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, "selected_files.zip");
  };

  const selectedCount = selectedSheets.size;

  // Split products into prioritized and others, both sorted alphabetically
  const { prioritizedProducts, otherProducts } = useMemo(() => {
    const sortAlphabetically = (arr) => [...arr].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const prioritized = products.filter(p => p.prioritized === true);
    const others = products.filter(p => p.prioritized !== true);

    return {
      prioritizedProducts: sortAlphabetically(prioritized),
      otherProducts: sortAlphabetically(others)
    };
  }, [products]);

  const renderProductList = (product, pIndex) => {
    const pid = product?.id ?? `p-${pIndex}`;
    const sheets = (product?.safetySheets ?? []);
    const filteredSheets = sheets.filter(passesFilters);

    return (
      <div key={pid} className="mb-4 border-bottom pb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0 fw-bold">{product.name}</h5>
          {filteredSheets.length > 0 && (
            <Form.Check
              type="checkbox"
              id={`select-all-list-${pid}`}
              label={l("Markera alla", "Select all")}
              checked={productAllSelected(product, pIndex)}
              onChange={() => toggleSelectAllForProduct(product, pIndex)}
            />
          )}
        </div>

        {product.description && (
          <p className="text-muted small mb-2">{product.description}</p>
        )}

        {filteredSheets.length > 0 ? (
          <div className="d-flex flex-column gap-1">
            {filteredSheets.map((sheet, sIdx) => {
              const origIdx = sheets.indexOf(sheet);
              const key = `${pid}-${origIdx}`;
              const isChecked = selectedSheets.has(key);
              return (
                <div key={key} className="d-flex align-items-center gap-2 py-1">
                  <Form.Check
                    type="checkbox"
                    id={`sheet-list-${key}`}
                    checked={isChecked}
                    onChange={() =>
                      toggleSheet(key, { url: sheet.file, name: sheet.name, productName: product.name })
                    }
                  />
                  <Link to={sheet.file} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    {l(sheet.titleSv, sheet.titleEn) || l("Namnlöst datablad", "Untitled sheet")}
                  </Link>
                  <small className="text-muted ms-auto">
                    {sheet.language && `${sheet.language}`}
                    {sheet.type && ` • ${sheet.type}`}
                  </small>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mb-0 text-muted small">
            {l("Inga datablad tillgängliga för aktuella filter.", "No sheets available for current filters.")}
          </p>
        )}
      </div>
    );
  };

  const languageMap = {
    swedish: l("Svenska", "Swedish"),
    english: l("Engelska", "English"),
    spanish: l("Spanska", "Spanish"),
    norwegian: l("Norska", "Norwegian"),
  };

      // <option value="safetysheet">{l("Säkerhetsdatablad", "Safety Datasheet")}</option>
      // <option value="safetysheet-hardener">{l("Säkerhetsdatablad härdare", "Safety Datasheet Hardener")}</option>
      // <option value="technical-datasheet">{l("Tekniskt Datablad", "Technical Datasheet")}</option>
      // <option value="productsheet">{l("Produktblad", "Product sheet")}</option>
      // <option value="other">{l("Övrigt", "Other")}</option>

  const typeMap = {
    "safetysheet": l("Säkerhetsdatablad", "Safety Datasheet"),
    "safetysheet-hardener": l("Säkerhetsdatablad härdare", "Safety Datasheet Hardener"),
    "technical-datasheet": l("Tekniskt Datablad", "Technical Datasheet"),
    "productsheet": l("Produktblad", "Product sheet"),
    "other": l("Övrigt", "Other"),
  };

  const getSheetTitle = (sheet) => {
    const title = l(sheet.titleSv, sheet.titleEn);
    const lang = sheet.language;
    const type = sheet.type;
    if (!title && !lang && !type) return sheet.name.replace(".pdf", "");


    return (
      <>
        {l(sheet.titleSv, sheet.titleEn)}
        {sheet.language && ` • ${languageMap[sheet.language.toLowerCase()] || sheet.language}`}
        {sheet.type && ` • ${typeMap[sheet.type.toLowerCase()] || sheet.type}`}
      </>
    );
  };

  const renderProductCard = (product, pIndex) => {
    const pid = product?.id ?? `p-${pIndex}`;
    const sheets = (product?.safetySheets ?? []);
    const filteredSheets = sheets.filter(passesFilters);

    return (
      <Col key={pid} xs={12} sm={6} lg={4}>
        <Card className="h-100 shadow-sm">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Card.Title className="mb-0">{product.name}</Card.Title>
              {filteredSheets.length > 0 && (
                <Form.Check
                  type="checkbox"
                  id={`select-all-${pid}`}
                  label={l("Markera alla", "Select all")}
                  checked={productAllSelected(product, pIndex)}
                  onChange={() => toggleSelectAllForProduct(product, pIndex)}
                />
              )}
            </div>

            {product.description && (
              <Card.Text>{product.description}</Card.Text>
            )}

            {filteredSheets.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {filteredSheets.map((sheet, sIdx) => {
                  // Important: use the *original* index in the product for stable keys
                  const origIdx = sheets.indexOf(sheet);
                  const key = `${pid}-${origIdx}`;
                  const isChecked = selectedSheets.has(key);
                  return (
                    <div key={key} className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        id={`sheet-${key}`}
                        checked={isChecked}
                        onChange={() =>
                          toggleSheet(key, { url: sheet.file, name: sheet.name, productName: product.name })
                        }
                      />
                      <Link to={sheet.file} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} className="flex-grow-1">
                        <small className="ms-1">
                          {getSheetTitle(sheet)}
                          {/* {l(sheet.titleSv, sheet.titleEn)}
                          {sheet.language && `• ${sheet.language}`}
                          {sheet.type && ` • ${sheet.type}`} */}
                        </small>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mb-0">
                {l("Inga datablad tillgängliga för aktuella filter.", "No sheets available for current filters.")}
              </p>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Container className="my-lg-5 my-3">
        <div className="mb-4 d-flex flex-wrap align-items-center gap-3">
          <h1 className="mb-0 fw-bold">
            {l("Säkerhetsdatablad och Tekniska datablad", "Safetysheets and technical data sheets")}
          </h1>
          <div className="ms-auto d-flex gap-2">
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('card')}
            >
              {l("Kortvy", "Card View")}
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              {l("Listvy", "List View")}
            </Button>
          </div>
        </div>
        <Row>
          <Col xl={4} lg={5} md={6} sm={8} xs={12} className="mb-3">
            <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
              <Form.Select
                size="sm"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                style={{ minWidth: 160 }}
              >
                {languages.map(opt => (
                  <option key={opt} value={opt}>
                    {opt === 'all' ? l("Alla språk", "All languages") : opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                size="sm"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{ minWidth: 160 }}
              >
                {types.map(opt => (
                  <option key={opt} value={opt}>
                    {opt === 'all' ? l("Alla typer", "All types") : typeMap[opt] || opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </Form.Select>

              <Button
                variant="primary"
                onClick={handleDownloadSelected}
                disabled={selectedCount === 0}
              >
                {l(`Ladda ner valda (${selectedCount})`, `Download selected (${selectedCount})`)}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={clearAll}
                disabled={selectedCount === 0}
              >
                {l("Rensa val", "Clear selection")}
              </Button>
            </div>
          </Col>
        </Row>

        {loading && <p>{l("Laddar...", "Loading...")}</p>}
        {error && <p className="text-danger">{l("Fel vid hämtning av data.", "Error fetching data.")}</p>}
        {!loading && !error && products.length === 0 && (
          <p>{l("Inga produkter datablad tillgängliga.", "No products sheets available.")}</p>
        )}

        {!loading && !error && prioritizedProducts.length > 0 && (
          <>
            <h2 className="mt-4 mb-3">{l("Huvudprodukter", "Main products")}</h2>
            {viewMode === 'card' ? (
              <Row className="g-4 align-items-start">
                {prioritizedProducts.map((product, pIndex) => renderProductCard(product, pIndex))}
              </Row>
            ) : (
              <div>
                {prioritizedProducts.map((product, pIndex) => renderProductList(product, pIndex))}
              </div>
            )}
          </>
        )}

        {!loading && !error && otherProducts.length > 0 && (
          <>
            <h2 className="mt-5 mb-3">{l("Alla produkter", "All products")}</h2>
            {viewMode === 'card' ? (
              <Row className="g-4 align-items-start">
                {otherProducts.map((product, pIndex) => renderProductCard(product, pIndex))}
              </Row>
            ) : (
              <div>
                {otherProducts.map((product, pIndex) => renderProductList(product, pIndex))}
              </div>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default About;
